'use client';
import { type SitewidePromotionProps } from './_SitewidePromotion';
import { Anchor } from '@repo/ui/src/components/Anchor';
import PinIcon from '@repo/ui/src/components/Icons/PinIcon';
import { LinkProps } from '@repo/ui/src/types/link';
import { gsap } from 'gsap';
import React from 'react';
import dynamic from 'next/dynamic';
import LanguageSelector from '../LanguageSelector';
import HeaderPaddingWrapper from '@repo/ui/src/components/HeaderPaddingWrapper';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { classNames } from '@repo/ui/src/utils/classNames';
import { appConfig } from '../../utils/appConfig';

const SitewidePromotion = dynamic(() => import('./_SitewidePromotion'));

export interface SitewideBannerProps {
  dealerLocatorLink: LinkProps;
  sitewidePromotions: Array<SitewidePromotionProps>;
  regions: Array<Region>;
  getTZDate?: (dateString: string) => Date;
}

const STANDARD_ROTATION_TIME = 5;
const LONG_ROTATION_TIME = 8;

export const isActivePromo = (
  promo: Pick<SitewidePromotionProps, 'countdownClock' | 'link'>,
  getTZDate?: (dateString: string) => Date,
) => {
  if (
    !promo?.countdownClock ||
    !promo?.countdownClock?.endDate ||
    !promo?.countdownClock?.endTime
  ) {
    return true;
  }
  if (!promo?.link) {
    return false;
  }
  const { countdownClock } = promo;
  if (!getTZDate) return true;
  const start = getTZDate(`${countdownClock.startDate} ${countdownClock.startTime}`);
  const end = getTZDate(`${countdownClock.endDate} ${countdownClock.endTime}`);
  const now = new Date();
  return now > start && now < end;
};

const SitewideBanner = ({
  dealerLocatorLink,
  sitewidePromotions,
  regions,
  getTZDate,
}: SitewideBannerProps) => {
  const promotions = React.useMemo(
    () =>
      sitewidePromotions?.filter((promo) => isActivePromo(promo, getTZDate) && !promo.isDefault),
    [sitewidePromotions, getTZDate],
  );
  const defaultPromo = sitewidePromotions?.find((promo) => promo.isDefault);
  const rotator = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const t1 = gsap?.timeline({ repeat: -1 });

    if (!promotions || promotions.length === 1) {
      return;
    }

    promotions.forEach((promo, i) => {
      const time = promo.rotationTime === 'long' ? LONG_ROTATION_TIME : STANDARD_ROTATION_TIME;
      const target = `.slider-${i}`;
      if (i > 0) {
        gsap.set(target, { x: 100, opacity: 0, pointerEvents: 'none' });
        t1.to(target, { x: 0, opacity: 1, pointerEvents: 'all' }, '>');
      } else {
        t1.fromTo(target, { x: 100, opacity: 0 }, { x: 0, opacity: 1 }, '>');
      }
      t1.addLabel(target);
      t1.to(target, { x: -100, opacity: 0, pointerEvents: 'none', delay: time }, '>');
    });

    if (rotator.current) {
      rotator.current.addEventListener('mouseenter', () => {
        // Pause the rotation so someone can click it
        t1.pause();
      });

      rotator.current.addEventListener('mouseleave', () => {
        t1.play();
      });
    }

    return () => {
      t1.kill();
    };
  }, [promotions]);

  return (
    <section
      className={classNames(
        'tw-py-2 tw-z-[1002] tw-text-left tw-relative tw-items-center tw-w-full tw-max-w-screen md:tw-inline-block',
        appConfig.isTraeger && 'tw-bg-[#25282A]',
        appConfig.isMeater && 'tw-bg-[#c8102e]',
      )}
      aria-label={'Sitewide Banner for promotional and emergency messaging'}
      data-testid="site-wide-banner"
    >
      <HeaderPaddingWrapper className="tw-flex tw-justify-between tw-items-center">
        <div className="tw-grid tw-grid-cols-[1fr_auto]">
          <div className="tw-w-full tw-overflow-hidden tw-inline-block" ref={rotator}>
            {promotions?.length && (
              <ul
                className="tw-m-0 tw-p-0 tw-relative tw-flex tw-flex-nowrap tw-gap-x-2"
                role="presentation"
              >
                {promotions.map((promo, i) => (
                  <SitewidePromotion key={i} {...promo} index={i} />
                ))}
                {!promotions.length && defaultPromo && <SitewidePromotion {...defaultPromo} />}
              </ul>
            )}
            {!promotions?.length && defaultPromo && <SitewidePromotion {...defaultPromo} />}
          </div>
        </div>
        <div className="tw-hidden sm:tw-flex tw-flex-row tw-gap-6">
          {dealerLocatorLink?.displayName && (
            <div className="tw-grid tw-gap-7 tw-items-center">
              <Anchor
                {...dealerLocatorLink}
                className={classNames(
                  'tw-group tw-text-white tw-flex tw-gap-2 tw-w-max tw-items-center',
                  appConfig.isTraeger ? 'hover:!tw-text-brand-200' : 'hover:!tw-text-neutral-600',
                )}
              >
                <PinIcon className="tw-transition-colors tw-h-4" />
                <span
                  className={classNames(
                    'tw-text-warm-100 tw-text-xs tw-leading-body tw-w-full tw-underline tw-transition-colors',
                    appConfig.isTraeger
                      ? 'group-hover:!tw-text-brand-200'
                      : 'group-hover:!tw-text-neutral-600',
                  )}
                >
                  {dealerLocatorLink?.displayName}
                </span>
              </Anchor>
            </div>
          )}
          <LanguageSelector regions={regions} />
        </div>
      </HeaderPaddingWrapper>
    </section>
  );
};

export default SitewideBanner;
